import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppButton } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icons';
import { StatusDot } from '@/shared/ui/status';
import { AppText } from '@/shared/ui/text';
import { spacing, useTheme } from '@/shared/ui/theme';
import {
  camerasOfDepartment,
  isWatchable,
  useCameraCatalog,
} from '../lib/catalog';

/**
 * Камеры отдела на его карточке — и вход в их просмотр одним нажатием.
 *
 * Отдел — это место, а камеры стоят в местах: «что сейчас на складе» начинается с
 * карточки склада, а не с поиска его камер в общем списке. Камера относится к отделу
 * через своё устройство (регистратор, камеру), которому отдел назначен в настройках.
 *
 * Пока камер у отдела нет, блока нет вовсе: пустая рамка «камер нет» на карточке
 * отдела бухгалтерии — это шум.
 */
const styles = StyleSheet.create({
  card: { gap: spacing(3) },
  head: { flexDirection: 'row', alignItems: 'center', gap: spacing(2) },
  headText: { flex: 1, gap: 2 },
  list: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2) },
  item: { flexDirection: 'row', alignItems: 'center', gap: spacing(1) },
});

export function DepartmentCameras({
  companyId,
  departmentIds,
  onWatch,
}: {
  companyId: string;
  /** Id отдела в обоих пространствах (sv_organization и проекция sv_control). */
  departmentIds: string[];
  onWatch: () => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const { catalog } = useCameraCatalog(companyId, 'device', {
    recorder: t('live.recorder.badge'),
    cameras: t('live.wall.cameras'),
    terminals: t('live.wall.terminals'),
    noChannels: t('live.recorder.noChannels'),
    noDepartment: t('live.view.noDepartment'),
  });
  const cameras = camerasOfDepartment(catalog, departmentIds);
  if (cameras.length === 0) {
    return null;
  }
  const online = cameras.filter(isWatchable).length;

  return (
    <Card padding={4} style={styles.card} testID="department-cameras">
      <View style={styles.head}>
        <Icon name="camera" size={18} color={p.foreground} />
        <View style={styles.headText}>
          <AppText variant="subtitle">
            {t('live.view.departmentCameras')}
          </AppText>
          <AppText variant="caption">
            {t('live.view.departmentCamerasCount', {
              online,
              count: cameras.length,
            })}
          </AppText>
        </View>
        <AppButton
          title={t('live.view.watch')}
          icon="play"
          size="sm"
          onPress={onWatch}
          disabled={online === 0}
          testID="department-cameras-watch"
        />
      </View>
      <View style={styles.list}>
        {cameras.map(c => (
          <View key={c.deviceId} style={styles.item}>
            <StatusDot
              tone={!c.available ? 'neutral' : c.online ? 'online' : 'offline'}
              size={7}
            />
            <AppText variant="caption">{c.name || c.deviceId}</AppText>
          </View>
        ))}
      </View>
    </Card>
  );
}
